import React, { useState, useEffect, useCallback } from 'react';
import { Container } from 'react-bootstrap';
import DashboardHeader from '../components/DashboardHeader';
import DashboardSidebar from '../components/DashboardSidebar';
import AthletePicker from '../components/AthletePicker';
import { useAuth } from '../../contexts/AuthContext';
import '../styles/FeaturedAthletesScreen.css';
import { useLocation } from 'react-router-dom';
import { uploadVideoToCloudflare } from '../../utils/cloudflareUpload';
import Cropper from 'react-easy-crop';

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', (e) => reject(e));
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = url;
  });

async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92));
}

const FeaturedAthletesScreen = ({ setActiveScreen }) => {
  const {
    getFeaturedAthletes,
    getSpotlights,
    addToFeatured,
    moveFeaturedUp,
    moveFeaturedDown,
    toggleFeaturedVisibility,
    removeFromFeatured,
    updateSpotlight,
    deleteSpotlight,
    uploadImage,
    getAthletesByIds,
    loading
  } = useAuth();

  const [activeItem, setActiveItem] = useState('athletes');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [featuredAthletes, setFeaturedAthletes] = useState([]);
  const [availableSpotlights, setAvailableSpotlights] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [editingAthlete, setEditingAthlete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editErrors, setEditErrors] = useState({});
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [spotlightDeleteConfirm, setSpotlightDeleteConfirm] = useState(null); // { id, name }
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [linkedMap, setLinkedMap] = useState({});
const location=useLocation();

  // Resolve linked athlete profiles for the featured list.
  useEffect(() => {
    const ids = [...new Set(featuredAthletes.map((a) => a.spotlight?.athlete_user_id).filter(Boolean))];
    if (ids.length === 0) { setLinkedMap({}); return; }
    getAthletesByIds(ids).then((res) => {
      if (!res.success) return;
      const map = {};
      res.data.forEach((a) => { map[a.user_id] = a; });
      setLinkedMap(map);
    });
  }, [featuredAthletes, getAthletesByIds]);
  // Fetch featured athletes and available spotlights
  useEffect(() => {
    fetchData();
  }, []);
  useEffect(() => {
    if (setActiveItem) {
      if (location.pathname.includes('/dashboard/nominations')) {
        setActiveItem('nominations');
      } else if (location.pathname.includes('/dashboard/featured')) {
        setActiveItem('athletes');
      } else if (location.pathname.includes('/dashboard/analytics')) {
        setActiveItem('analytics');
      } else if (location.pathname.includes('/dashboard/email-list')) {
        setActiveItem('email-list');
      }
    }
  }, [location.pathname, setActiveItem]);
  const fetchData = async () => {
    try {
      const [featuredResult, spotlightsResult] = await Promise.all([
        getFeaturedAthletes(),
        getSpotlights('published')
      ]);

      if (featuredResult.success) {
        setFeaturedAthletes(featuredResult.data || []);
      } else {
        showNotification('error', `Failed to load featured athletes: ${featuredResult.error || 'Unknown error'}`);
      }

      if (spotlightsResult.success) {
        // Filter out spotlights that are already featured
        const featuredIds = (featuredResult.data || []).map(f => f.spotlight_id);
        const available = (spotlightsResult.data || []).filter(
          s => !featuredIds.includes(s.id)
        );
        setAvailableSpotlights(available);
      } else {
        showNotification('error', `Failed to load spotlights: ${spotlightsResult.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showNotification('error', 'Failed to load data. Please try again.');
    }
  };

  // Show notification
  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: '', message: '' });
    }, 5000);
  };

  const handleMoveUp = async (id) => {
    setActionLoading(true);
    try {
      const result = await moveFeaturedUp(id);
      if (result.success) {
        await fetchData();
      } else {
        showNotification('error', `Failed to move athlete up: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error moving athlete up:', error);
      showNotification('error', 'Failed to move athlete. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMoveDown = async (id) => {
    setActionLoading(true);
    try {
      const result = await moveFeaturedDown(id);
      if (result.success) {
        await fetchData();
      } else {
        showNotification('error', `Failed to move athlete down: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error moving athlete down:', error);
      showNotification('error', 'Failed to move athlete. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleVisibility = async (id) => {
    setActionLoading(true);
    try {
      const result = await toggleFeaturedVisibility(id);
      if (result.success) {
        await fetchData();
      } else {
        showNotification('error', `Failed to toggle visibility: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error toggling visibility:', error);
      showNotification('error', 'Failed to change visibility. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemove = async (id) => {
    if (window.confirm('Are you sure you want to remove this athlete from featured?')) {
      setActionLoading(true);
      try {
        const result = await removeFromFeatured(id);
        if (result.success) {
          showNotification('success', 'Athlete removed from featured');
          await fetchData();
        } else {
          showNotification('error', `Failed to remove athlete: ${result.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error removing athlete:', error);
        showNotification('error', 'Failed to remove athlete. Please try again.');
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleDelete = async () => {
    if (!spotlightDeleteConfirm) return;
    setSpotlightDeleteConfirm(null);
    setActionLoading(true);
    try {
      const result = await deleteSpotlight(spotlightDeleteConfirm.id);
      if (result.success) {
        showNotification('success', 'Spotlight deleted successfully');
        await fetchData();
      } else {
        showNotification('error', `Failed to delete spotlight: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      showNotification('error', 'Failed to delete spotlight. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddToFeatured = async (spotlightId) => {
    setActionLoading(true);
    try {
      const result = await addToFeatured(spotlightId);
      if (result.success) {
        showNotification('success', 'Athlete added to featured');
        await fetchData();
      } else {
        showNotification('error', `Failed to add to featured: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error adding to featured:', error);
      showNotification('error', 'Failed to add athlete to featured. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = (athlete) => {
    // Transform existing media to have both url and preview for display
    let rawMedia = athlete.spotlight?.moment_media || [];
    console.log('Raw moment_media from database:', rawMedia);

    // Handle case where moment_media might be stored as JSON string
    if (typeof rawMedia === 'string') {
      try {
        rawMedia = JSON.parse(rawMedia);
      } catch (e) {
        console.error('Failed to parse moment_media:', e);
        rawMedia = [];
      }
    }

    // Ensure it's an array
    if (!Array.isArray(rawMedia)) {
      console.warn('moment_media is not an array:', rawMedia);
      rawMedia = [];
    }

    // moment_media now only contains images (not video, not hero)
    const existingImages = rawMedia.map(media => {
      if (typeof media === 'string') {
        // Plain URL string
        if (media.startsWith('{')) {
          try {
            const parsed = JSON.parse(media);
            return {
              url: parsed.url,
              preview: parsed.url,
              type: 'image',
              fromDatabase: true
            };
          } catch (e) {
            return {
              url: media,
              preview: media,
              type: 'image',
              fromDatabase: true
            };
          }
        }
        return {
          url: media,
          preview: media,
          type: 'image',
          fromDatabase: true
        };
      }
      return {
        url: media.url,
        preview: media.url,
        type: 'image',
        fromDatabase: true
      };
    });

    // Add hero image as the first image in the list
    const heroImageUrl = athlete.spotlight?.hero_image;
    const allImages = heroImageUrl
      ? [
          { url: heroImageUrl, preview: heroImageUrl, type: 'image', fromDatabase: true },
          ...existingImages
        ]
      : existingImages;

    console.log('Loaded images:', allImages);
    console.log('Video UID:', athlete.spotlight?.video_uid);

    setEditingAthlete({
      ...athlete,
      nominationVideoUid: athlete.spotlight?.video_uid || null,
      uploadedVideoFile: null,
      uploadedVideoUid: null,
      uploadedVideoPreview: null,
      momentHeadline: athlete.spotlight?.moment_headline || 'The Moment',
      momentDescription: athlete.spotlight?.moment_description || '',
      momentMedia: allImages,
      selectedHeroImageIndex: allImages.length > 0 ? 0 : null, // First image is hero
      officialCaption: athlete.spotlight?.official_caption || '',
      timeLabel: athlete.spotlight?.time_label || '',
      regionalLabel: athlete.spotlight?.regional_label || ''
    });
    setEditErrors({});
    setVideoUploadProgress(0);
    setIsUploadingVideo(false);
    setShowEditModal(true);
  };

  // Helper function to validate YouTube URL
  const isValidYouTubeUrl = (url) => {
    if (!url) return false;
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/)|youtu\.be\/)[\w-]+/;
    return youtubeRegex.test(url);
  };

  // Validate edit form
  const validateEditForm = () => {
    const newErrors = {};

    if (!editingAthlete.spotlight?.athlete_name?.trim()) {
      newErrors.athlete_name = 'Athlete name is required';
    }

    if (!editingAthlete.spotlight?.primary_sport?.trim()) {
      newErrors.primary_sport = 'Primary sport is required';
    }

    if (!editingAthlete.officialCaption?.trim()) {
      newErrors.officialCaption = 'Official caption is required';
    }

    // Hero image validation - check if one is selected from moment media
    if (editingAthlete.selectedHeroImageIndex === null) {
      newErrors.heroImage = 'Please select one image as the hero image';
    }

    // Validate moment media - Required: 1-3 images
    if (!editingAthlete.momentMedia || editingAthlete.momentMedia.length === 0) {
      newErrors.momentMedia = 'At least 1 image is required';
    } else if (editingAthlete.momentMedia.length > 3) {
      newErrors.momentMedia = 'Maximum 3 images allowed';
    } else {
      // Validate individual image files
      editingAthlete.momentMedia.forEach((media, index) => {
        if (media.file && media.file instanceof File) {
          const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
          if (!validTypes.includes(media.file.type)) {
            newErrors[`momentMedia_${index}`] = `Invalid file type for image ${index + 1}`;
          }

          const maxSize = 5 * 1024 * 1024; // 5MB for images
          if (media.file.size > maxSize) {
            newErrors[`momentMedia_${index}`] = `Image ${index + 1} exceeds 5MB limit`;
          }
        }
      });
    }

    setEditErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveEdit = async () => {
    if (!editingAthlete) return;

    // Validate form first
    if (!validateEditForm()) {
      // Scroll to first error
      setTimeout(() => {
        const firstError = Object.keys(editErrors)[0];
        if (firstError) {
          const element = document.querySelector(`[data-edit-error="${firstError}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 100);
      return;
    }

    setActionLoading(true);
    setEditErrors({});

    try {
      // Upload video first if there's a file to upload
      let uploadedVideoUid = null;
      if (editingAthlete.uploadedVideoFile) {
        setIsUploadingVideo(true);
        setVideoUploadProgress(0);

        try {
          showNotification('success', 'Uploading video...');
          const videoResult = await uploadVideoToCloudflare(
            editingAthlete.uploadedVideoFile,
            setVideoUploadProgress
          );

          if (videoResult && videoResult.uid) {
            uploadedVideoUid = videoResult.uid;
            showNotification('success', 'Video uploaded successfully!');
          } else {
            throw new Error('Video upload failed');
          }
        } catch (videoError) {
          console.error('Video upload error:', videoError);
          showNotification('error', 'Failed to upload video. Please try again.');
          setActionLoading(false);
          setIsUploadingVideo(false);
          setVideoUploadProgress(0);
          return;
        } finally {
          setIsUploadingVideo(false);
        }
      }

      // Hero image will be extracted from moment media after all images are processed
      let heroImageUrl = null;

      // Process moment media - upload new files, keep existing URLs
      let allProcessedImages = [];
      if (editingAthlete.momentMedia && editingAthlete.momentMedia.length > 0) {
        for (const media of editingAthlete.momentMedia) {
          if (media.url && media.fromDatabase) {
            // Keep existing URL from database
            allProcessedImages.push(media.url);
          } else if (media.file) {
            // Upload new file
            try {
              const uploadResult = await uploadImage(media.file);
              if (uploadResult.success) {
                allProcessedImages.push(uploadResult.url);
              }
            } catch (error) {
              console.warn('Error uploading image:', error);
            }
          }
        }
      }

      // Separate hero image from moment media
      let finalMomentMedia = [];
      if (editingAthlete.selectedHeroImageIndex !== null && allProcessedImages[editingAthlete.selectedHeroImageIndex]) {
        // Extract hero image from the selected index
        heroImageUrl = allProcessedImages[editingAthlete.selectedHeroImageIndex];

        // Remove hero image from moment_media array
        finalMomentMedia = allProcessedImages.filter((_, index) => index !== editingAthlete.selectedHeroImageIndex);
      }

      // Get video UID (prioritize newly uploaded video, then existing video)
      const videoUid = uploadedVideoUid || editingAthlete.nominationVideoUid || null;

      console.log('Updating spotlight with:', {
        heroImage: heroImageUrl,
        videoUid: videoUid,
        momentMedia: finalMomentMedia
      });

      const result = await updateSpotlight(editingAthlete.spotlight_id, {
        athlete_name: editingAthlete.spotlight?.athlete_name,
        athlete_user_id: editingAthlete.spotlight?.athlete_user_id || null,
        primary_sport: editingAthlete.spotlight?.primary_sport,
        secondary_sports: editingAthlete.spotlight?.secondary_sports,
        city: editingAthlete.spotlight?.city,
        state: editingAthlete.spotlight?.state,
        hero_image: heroImageUrl,
        video_uid: videoUid,
        moment_headline: 'The Moment',
        moment_description: 'This athlete earned their spotlight through consistency, leadership, and showing up when it mattered.',
        moment_media: finalMomentMedia.map(url => ({ url, type: 'image' })),
        official_caption: editingAthlete.officialCaption,
        time_label: editingAthlete.timeLabel,
        regional_label: editingAthlete.regionalLabel || ''
      });

      if (result.success) {
        showNotification('success', 'Spotlight updated successfully');
        setShowEditModal(false);
        setEditingAthlete(null);
        await fetchData();
      } else {
        throw new Error(result.error || 'Failed to update spotlight');
      }
    } catch (error) {
      console.error('Error updating spotlight:', error);
      showNotification('error', error.message || 'Failed to update spotlight. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleHeroImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validImageTypes.includes(file.type)) {
        setEditErrors(prev => ({
          ...prev,
          heroImage: 'Invalid image format. Please use JPEG, PNG, or WebP'
        }));
        return;
      }
      
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setEditErrors(prev => ({
          ...prev,
          heroImage: 'Image size exceeds 10MB limit'
        }));
        return;
      }

      // Clear any previous error for hero image
      setEditErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.heroImage;
        return newErrors;
      });

      const preview = URL.createObjectURL(file);
      setEditingAthlete(prev => ({
        ...prev,
        heroImage: file,
        heroImagePreview: preview
      }));
    }
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleCropConfirm = async () => {
    try {
      const blob = await getCroppedImg(rawImageSrc, croppedAreaPixels);
      const preview = URL.createObjectURL(blob);
      const file = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
      setEditingAthlete(prev => {
        const updated = [...(prev.momentMedia || []), { file, preview, type: 'image' }];
        return {
          ...prev,
          momentMedia: updated,
          selectedHeroImageIndex: prev.selectedHeroImageIndex === null ? 0 : prev.selectedHeroImageIndex,
        };
      });
    } catch (e) {
      console.error('Crop error:', e);
    } finally {
      setCropModalOpen(false);
      setRawImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    }
  };

  const handleAddMedia = (file) => {
    const currentMedia = editingAthlete.momentMedia || [];
    if (currentMedia.length >= 3) { showNotification('error', 'Maximum 3 images allowed'); return; }
    if (!file.type.startsWith('image/')) { showNotification('error', 'Only image files are allowed.'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setRawImageSrc(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
  };

  // Clear error when field changes
  const handleFieldChange = (field, value, isSpotlightField = false) => {
    if (isSpotlightField) {
      setEditingAthlete(prev => ({
        ...prev,
        spotlight: { ...prev.spotlight, [field]: value }
      }));
    } else {
      setEditingAthlete(prev => ({ ...prev, [field]: value }));
    }
    
    // Clear error for this field if it exists
    if (editErrors[field]) {
      setEditErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle video selection (preview only, upload happens on save)
  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validVideoTypes = ['video/mp4', 'video/quicktime', 'video/x-m4v'];
    if (!validVideoTypes.includes(file.type)) {
      showNotification('error', 'Invalid video format. Please use MP4 or MOV');
      return;
    }

    // Validate file size (200MB)
    if (file.size > 200 * 1024 * 1024) {
      showNotification('error', 'Video size exceeds 200MB limit');
      return;
    }

    // Just store the file and create preview - don't upload yet
    setEditingAthlete(prev => ({
      ...prev,
      uploadedVideoFile: file,
      uploadedVideoPreview: URL.createObjectURL(file),
      nominationVideoUid: null, // Clear existing video when selecting new one
      uploadedVideoUid: null
    }));
    setVideoUploadProgress(0);
  };

  const rules = [
    'No maximum number of featured athletes',
    'Scrollable grid on public page',
    'Only published spotlights can be featured',
    'Unpublishing auto-removes from grid',
    'No gaps allowed in ordering',
  ];

  return (
    <div className="featured-athletes-screen">
      {/* Notification Component */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.type === 'success' && '✓ '}
          {notification.type === 'error' && '✗ '}
          {notification.message}
        </div>
      )}

      <DashboardSidebar
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
        setActiveScreen={setActiveScreen}
      />

      <div className="featured-athletes-screen-content">
        <DashboardHeader
          toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          title="Featured Athletes Manager"
          subtitle="Manage and reorder featured athletes displayed on the public page"
        />

        <Container fluid className="featured-athletes-screen-container">
          <div className="featured-athletes-main-content">
            {/* Featured Athletes Section */}
            <div className="featured-athletes-section">
              <div className="featured-athletes-section-header">
                <h2 className="featured-athletes-section-title">Featured Athletes</h2>
                <span className="featured-athletes-count">
                  {String(featuredAthletes.length).padStart(2, '0')} Athletes
                </span>
              </div>

              {/* Loading State */}
              {loading && (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <p>Loading featured athletes...</p>
                </div>
              )}

              {/* Empty State */}
              {!loading && featuredAthletes.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  <p>No featured athletes yet. Add spotlights from the sidebar.</p>
                </div>
              )}

              <div className="featured-athletes-list">
                {featuredAthletes.map((athlete, index) => (
                  <div 
                    key={athlete.id} 
                    className="featured-athlete-card"
                    style={{ opacity: athlete.is_visible ? 1 : 0.5 }}
                  >
                    <div className="featured-athlete-info">
                      <img
                        src={athlete.spotlight?.hero_image || '/assets/images/athlete-placeholder.png'}
                        alt={athlete.spotlight?.athlete_name}
                        className="featured-athlete-image"
                      />
                      <div className="featured-athlete-details">
                        <h3 className="featured-athlete-name">
                          {athlete.spotlight?.athlete_name}
                          {!athlete.is_visible && <span style={{ color: '#999', fontSize: '12px' }}> (Hidden)</span>}
                        </h3>
                        <p className="featured-athlete-sport">{athlete.spotlight?.primary_sport}</p>
                        <p className="featured-athlete-position">Order Position: {athlete.order_position}</p>
                        {athlete.spotlight?.athlete_user_id ? (
                          (() => {
                            const la = linkedMap[athlete.spotlight.athlete_user_id];
                            return (
                              <div className="featured-linked-chip">
                                {la?.profile_image ? (
                                  <img src={la.profile_image} alt="" className="featured-linked-thumb" />
                                ) : (
                                  <span className="featured-linked-thumb featured-linked-thumb--initial">
                                    {(la?.full_name || '?').charAt(0).toUpperCase()}
                                  </span>
                                )}
                                <div className="featured-linked-info">
                                  <span className="featured-linked-name">{la?.full_name || 'Linked athlete'}</span>
                                  <span className="featured-linked-email">{la?.email || '—'}</span>
                                </div>
                                <span className="featured-linked-badge">Linked</span>
                              </div>
                            );
                          })()
                        ) : (
                          <span className="featured-linked-none">Not linked to a profile</span>
                        )}
                      </div>
                    </div>

                    <div className="featured-athlete-actions">
                      <div className="featured-athlete-move-buttons">
                        <button 
                          className="featured-athlete-move-btn"
                          onClick={() => handleMoveUp(athlete.id)}
                          disabled={actionLoading || index === 0}
                          style={{ opacity: index === 0 ? 0.3 : 1 }}
                        >
                          <img src="/assets/images/arrow-up-icon.png" alt="Move Up" />
                        </button>
                        <button 
                          className="featured-athlete-move-btn"
                          onClick={() => handleMoveDown(athlete.id)}
                          disabled={actionLoading || index === featuredAthletes.length - 1}
                          style={{ opacity: index === featuredAthletes.length - 1 ? 0.3 : 1 }}
                        >
                          <img src="/assets/images/arrow-down-icon.png" alt="Move Down" />
                        </button>
                      </div>
                      <div className="featured-athlete-action-buttons">
                        <button
                          className="featured-athlete-action-btn"
                          onClick={() => handleToggleVisibility(athlete.id)}
                          disabled={actionLoading}
                          title={athlete.is_visible ? 'Hide' : 'Show'}
                        >
                          <img
                            src={athlete.is_visible ? "/assets/images/hide-icon.png" : "/assets/images/eye-icon.png"}
                            alt={athlete.is_visible ? "Hide" : "Show"}
                          />
                        </button>
                        <button 
                          className="featured-athlete-action-btn"
                          onClick={() => handleEdit(athlete)}
                          disabled={actionLoading}
                        >
                          <img src="/assets/images/edit-icon.png" alt="Edit" />
                        </button>
                        <button 
                          className="featured-athlete-action-btn featured-athlete-delete-btn"
                          onClick={() => handleRemove(athlete.id)}
                          disabled={actionLoading}
                        >
                          <img src="/assets/images/delete-icon.png" alt="Delete" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="featured-athletes-ordering-rules">
                <h4 className="featured-athletes-ordering-title">Ordering Rules:</h4>
                <p className="featured-athletes-ordering-text">
                  Use the up/down arrows to reorder. Removing an athlete automatically compacts the order (no gaps allowed).
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar Content */}
          <div className="featured-athletes-sidebar">
            {/* Available Spotlights */}
            <div className="featured-athletes-sidebar-section">
              <h3 className="featured-athletes-sidebar-title">Available Spotlights</h3>
              {availableSpotlights.length === 0 ? (
                <div className="featured-athletes-empty-state">
                  <p>All published spotlights are already featured.</p>
                </div>
              ) : (
                <div className="featured-athletes-available-list">
                  {availableSpotlights.map((spotlight) => (
                    <div 
                      key={spotlight.id} 
                      className="featured-athletes-available-item"
                    >
                      <div className="featured-athletes-available-info">
                        <img 
                          src={spotlight.hero_image || '/assets/images/athlete-placeholder.png'} 
                          alt={spotlight.athlete_name}
                          className="featured-athletes-available-image"
                        />
                        <div className="featured-athletes-available-text">
                          <p className="featured-athletes-available-name">{spotlight.athlete_name}</p>
                          <p className="featured-athletes-available-sport">{spotlight.primary_sport}</p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <button
                          onClick={() => handleAddToFeatured(spotlight.id)}
                          disabled={actionLoading}
                          className="featured-athletes-available-btn"
                        >
                          + Add
                        </button>
                        <button
                          onClick={() => setSpotlightDeleteConfirm({ id: spotlight.id, name: spotlight.athlete_name })}
                          disabled={actionLoading}
                          title="Delete spotlight"
                          style={{ padding: '7px 9px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '8px', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Featured Athletes Rules */}
            <div className="featured-athletes-rules-section">
              <h3 className="featured-athletes-rules-section-title">Featured Athletes Rules</h3>
              <ul className="featured-athletes-rules-section-list">
                {rules.map((rule, index) => (
                  <li key={index} className="featured-athletes-rules-section-item">
                    {rule}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </div>

      {/* Image Crop Modal */}
      {cropModalOpen && rawImageSrc && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'relative', width: '100%', maxWidth: '480px', height: '480px', borderRadius: '12px', overflow: 'hidden' }}>
            <Cropper
              image={rawImageSrc}
              crop={crop}
              zoom={zoom}
              aspect={3 / 4}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
          <div style={{ marginTop: '20px', width: '100%', maxWidth: '480px', padding: '0 16px' }}>
            <input type="range" min={1} max={3} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} style={{ width: '100%', accentColor: '#f3b129' }} />
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button onClick={() => { setCropModalOpen(false); setRawImageSrc(null); }} style={{ padding: '10px 28px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
              Cancel
            </button>
            <button onClick={handleCropConfirm} style={{ padding: '10px 28px', background: '#f3b129', border: 'none', borderRadius: '8px', color: '#000', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>
              Crop & Add
            </button>
          </div>
        </div>
      )}

      {/* Spotlight Delete Confirm Modal */}
      {spotlightDeleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#0e0e0e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', padding: '32px 28px', maxWidth: '380px', width: '90%', textAlign: 'center' }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '50%', margin: '0 auto 18px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(239,68,68,0.12)' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/>
              </svg>
            </div>
            <h2 style={{ color: '#fff', fontSize: '17px', fontWeight: '700', marginBottom: '8px' }}>Delete Spotlight?</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', lineHeight: '1.6', marginBottom: '24px' }}>
              Are you sure you want to permanently delete <span style={{ color: '#fff', fontWeight: '600' }}>{spotlightDeleteConfirm.name}</span>'s spotlight? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setSpotlightDeleteConfirm(null)} style={{ flex: 1, padding: '10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'rgba(255,255,255,0.7)', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleDelete} style={{ flex: 1, padding: '10px', background: '#ef4444', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingAthlete && (
        <>
          <div 
            className="nomination-modal-overlay" 
            onClick={() => setShowEditModal(false)}
          ></div>
          <div className="spotlight-editor-modal">
            <div className="spotlight-editor-header">
              <h2 className="spotlight-editor-title">
                Edit Spotlight - {editingAthlete.spotlight?.athlete_name}
              </h2>
              <button 
                className="spotlight-editor-close"
                onClick={() => setShowEditModal(false)}
              >
                <img src="/assets/images/close-modal-icon.png" alt="Close" />
              </button>
            </div>

            <div className="spotlight-editor-body">
              {/* Athlete Info */}
              <div className="spotlight-editor-section">
                <label className="spotlight-editor-label">Athlete Information</label>
                <label className="spotlight-editor-sublabel">Athlete Name *</label>
                <input
                  type="text"
                  className={`spotlight-editor-input ${editErrors.athlete_name ? 'error' : ''}`}
                  placeholder="Athlete Name"
                  value={editingAthlete.spotlight?.athlete_name || ''}
                  onChange={(e) => handleFieldChange('athlete_name', e.target.value, true)}
                  data-edit-error="athlete_name"
                />
                {editErrors.athlete_name && (
                  <div className="error-message">{editErrors.athlete_name}</div>
                )}

                <label className="spotlight-editor-sublabel" style={{ marginTop: 14 }}>Link to Registered Athlete</label>
                <AthletePicker
                  value={editingAthlete.spotlight?.athlete_user_id || null}
                  linkedName={editingAthlete.spotlight?.athlete_name}
                  onChange={(userId, athlete) => {
                    handleFieldChange('athlete_user_id', userId || '', true);
                    if (athlete?.full_name && !editingAthlete.spotlight?.athlete_name?.trim()) {
                      handleFieldChange('athlete_name', athlete.full_name, true);
                    }
                  }}
                />
              </div>

              <div className="spotlight-editor-section">
                <label className="spotlight-editor-sublabel">Regional Spotlight Label</label>
                <input
                  type="text"
                  className="spotlight-editor-input"
                  placeholder="For example: Temecula Valley Spotlight"
                  value={editingAthlete.regionalLabel || ''}
                  onChange={(e) => handleFieldChange('regionalLabel', e.target.value)}
                />
              </div>

              <div className="spotlight-editor-section">
                <label className="spotlight-editor-sublabel">Primary Sport *</label>
                <input
                  type="text"
                  className={`spotlight-editor-input ${editErrors.primary_sport ? 'error' : ''}`}
                  placeholder="Primary Sport"
                  value={editingAthlete.spotlight?.primary_sport || ''}
                  onChange={(e) => handleFieldChange('primary_sport', e.target.value, true)}
                  data-edit-error="primary_sport"
                />
                {editErrors.primary_sport && (
                  <div className="error-message">{editErrors.primary_sport}</div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  <label className="spotlight-editor-sublabel">City</label>
                  <input
                    type="text"
                    className="spotlight-editor-input"
                    placeholder="City"
                    value={editingAthlete.spotlight?.city || ''}
                    onChange={(e) => handleFieldChange('city', e.target.value, true)}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="spotlight-editor-sublabel">State</label>
                  <input
                    type="text"
                    className="spotlight-editor-input"
                    placeholder="State"
                    value={editingAthlete.spotlight?.state || ''}
                    onChange={(e) => handleFieldChange('state', e.target.value, true)}
                  />
                </div>
              </div>

              {/* Spotlight Video */}
              <div className="spotlight-editor-section">
                <label className="spotlight-editor-sublabel">Spotlight Video (Optional)</label>

                {/* Show video preview - existing video (Cloudflare iframe) */}
                {editingAthlete.nominationVideoUid && !editingAthlete.uploadedVideoFile && (
                  <div className="spotlight-video-preview-box" style={{ position: 'relative' }}>
                    <iframe
                      src={`https://iframe.cloudflarestream.com/${editingAthlete.nominationVideoUid}`}
                      allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                      allowFullScreen
                      style={{
                        border: 'none',
                        width: '100%',
                        height: '300px',
                        borderRadius: '8px'
                      }}
                    />
                    <button
                      className="spotlight-video-remove-btn"
                      onClick={() => {
                        setEditingAthlete(prev => ({
                          ...prev,
                          nominationVideoUid: null,
                          uploadedVideoUid: null,
                          uploadedVideoFile: null,
                          uploadedVideoPreview: null
                        }));
                        setVideoUploadProgress(0);
                      }}
                      title="Remove video"
                    >
                      <img src="/assets/images/minus-icon.png" alt="Remove" />
                    </button>
                    <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <p className="spotlight-editor-media-note" style={{ margin: 0, flex: 1 }}>
                        Existing video will be included in the spotlight.
                      </p>
                      <button
                        className="spotlight-editor-select-btn"
                        onClick={() => document.getElementById('edit-video-input').click()}
                        style={{ fontSize: '13px', padding: '6px 14px' }}
                      >
                        Replace Video
                        <img src="/assets/images/upload-icon1.png" alt="Upload" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Show video preview - uploaded file (HTML5 video) */}
                {editingAthlete.uploadedVideoFile && (
                  <div className="spotlight-video-preview-box" style={{ position: 'relative' }}>
                    <video
                      src={editingAthlete.uploadedVideoPreview}
                      controls
                      style={{
                        width: '100%',
                        height: '300px',
                        borderRadius: '8px',
                        backgroundColor: '#000'
                      }}
                    />
                    <button
                      className="spotlight-video-remove-btn"
                      onClick={() => {
                        if (editingAthlete.uploadedVideoPreview) {
                          URL.revokeObjectURL(editingAthlete.uploadedVideoPreview);
                        }
                        setEditingAthlete(prev => ({
                          ...prev,
                          uploadedVideoFile: null,
                          uploadedVideoPreview: null,
                          uploadedVideoUid: null
                        }));
                        setVideoUploadProgress(0);
                      }}
                      title="Remove video"
                    >
                      <img src="/assets/images/minus-icon.png" alt="Remove" />
                    </button>
                    <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <p className="spotlight-editor-media-note" style={{ margin: 0, flex: 1 }}>
                        Video ready. Will be uploaded when you save the spotlight.
                      </p>
                      <button
                        className="spotlight-editor-select-btn"
                        onClick={() => document.getElementById('edit-video-input').click()}
                        style={{ fontSize: '13px', padding: '6px 14px' }}
                      >
                        Replace Video
                        <img src="/assets/images/upload-icon1.png" alt="Upload" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Show upload section if no video */}
                {!editingAthlete.nominationVideoUid && !editingAthlete.uploadedVideoFile && (
                  <div className="spotlight-editor-upload-area" onClick={() => document.getElementById('edit-video-input').click()}>
                    <div className="spotlight-editor-upload-icon">
                      <img src="/assets/images/upload-icon.png" alt="Upload" />
                    </div>
                    <p className="spotlight-editor-upload-text">Click to upload video</p>
                    <p className="spotlight-editor-upload-subtext">MP4, MOV up to 200MB</p>
                    <button className="spotlight-editor-select-btn" onClick={(e) => { e.stopPropagation(); document.getElementById('edit-video-input').click(); }}>
                      Select Video
                      <img src="/assets/images/upload-icon1.png" alt="Upload" />
                    </button>
                  </div>
                )}

                <input
                  type="file"
                  accept="video/mp4,video/quicktime,video/x-m4v"
                  onChange={handleVideoChange}
                  style={{ display: 'none' }}
                  id="edit-video-input"
                />
              </div>

              {/* Spotlight Images */}
              <div className="spotlight-editor-section">
                <label className="spotlight-editor-sublabel">Spotlight Images *</label>
                <p className="spotlight-editor-media-note">
                  {editingAthlete.momentMedia && editingAthlete.momentMedia.length > 0
                    ? 'Select one image as the hero image. You can upload up to 3 images total.'
                    : 'Upload 1-3 images and select one as the hero image.'}
                </p>
                <div className="spotlight-editor-media-grid">
                  {editingAthlete.momentMedia && editingAthlete.momentMedia.map((media, index) => {
                    const mediaSrc = media.url || media.preview;

                    return (
                      <div key={index} className={`spotlight-editor-media-item ${editingAthlete.selectedHeroImageIndex === index ? 'hero-selected' : ''}`}>
                        <img
                          src={mediaSrc}
                          alt={`Image ${index + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => { e.target.src = '/assets/images/placeholder-1.png'; }}
                        />
                        {/* Hero Badge */}
                        {editingAthlete.selectedHeroImageIndex === index && (
                          <div className="hero-badge">Hero Image</div>
                        )}
                        {/* Set as Hero Button */}
                        <button
                          className="spotlight-editor-media-hero-btn"
                          onClick={() => {
                            setEditingAthlete(prev => ({
                              ...prev,
                              selectedHeroImageIndex: index
                            }));
                          }}
                        >
                          {editingAthlete.selectedHeroImageIndex === index ? '★ Hero' : 'Set as Hero'}
                        </button>
                        {/* Remove Button */}
                        <button
                          className="spotlight-editor-media-remove"
                          onClick={() => {
                            setEditingAthlete(prev => ({
                              ...prev,
                              momentMedia: prev.momentMedia.filter((_, i) => i !== index),
                              selectedHeroImageIndex: prev.selectedHeroImageIndex === index ? null :
                                prev.selectedHeroImageIndex > index ? prev.selectedHeroImageIndex - 1 : prev.selectedHeroImageIndex
                            }));
                            // Clear error for this media if it exists
                            const errorKey = `momentMedia_${index}`;
                            if (editErrors[errorKey]) {
                              setEditErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors[errorKey];
                                return newErrors;
                              });
                            }
                          }}
                        >
                          <img src="/assets/images/minus-icon.png" alt="Remove" />
                        </button>
                        {editErrors[`momentMedia_${index}`] && (
                          <div className="error-message-small">{editErrors[`momentMedia_${index}`]}</div>
                        )}
                      </div>
                    );
                  })}
                  {(!editingAthlete.momentMedia || editingAthlete.momentMedia.length < 3) && (
                    <button
                      className="spotlight-editor-media-add"
                      onClick={() => document.getElementById('edit-moment-media-input').click()}
                    >
                      <img src="/assets/images/plus-icon.png" alt="Add" />
                      <span>Add Image</span>
                    </button>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => { if (e.target.files[0]) { handleAddMedia(e.target.files[0]); e.target.value = ''; } }}
                    style={{ display: 'none' }}
                    id="edit-moment-media-input"
                  />
                </div>
                {editErrors.momentMedia && (
                  <div className="error-message">{editErrors.momentMedia}</div>
                )}
                {editErrors.heroImage && (
                  <div className="error-message">{editErrors.heroImage}</div>
                )}
              </div>

              {/* Official Caption */}
              <div className="spotlight-editor-section">
                <label className="spotlight-editor-sublabel">Official Caption *</label>
                <textarea
                  className={`spotlight-editor-textarea ${editErrors.officialCaption ? 'error' : ''}`}
                  placeholder="Write the official caption for this Spotlight..."
                  rows="4"
                  value={editingAthlete.officialCaption || ''}
                  onChange={(e) => handleFieldChange('officialCaption', e.target.value)}
                  data-edit-error="officialCaption"
                ></textarea>
                {editErrors.officialCaption && (
                  <div className="error-message">{editErrors.officialCaption}</div>
                )}
              </div>

              {/* Time Label */}
              <div className="spotlight-editor-section">
                <label className="spotlight-editor-sublabel">Time Label</label>
                <input
                  type="text"
                  className="spotlight-editor-input"
                  placeholder="For example: Class of 2024, Spring 2023"
                  value={editingAthlete.timeLabel || ''}
                  onChange={(e) => handleFieldChange('timeLabel', e.target.value)}
                />
              </div>

            </div>

            <div className="spotlight-editor-footer">
              <button 
                className="spotlight-editor-cancel-btn"
                onClick={() => setShowEditModal(false)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className="spotlight-editor-publish-btn"
                onClick={handleSaveEdit}
                disabled={actionLoading}
              >
                <img src="/assets/images/check-icon.png" alt="Save" />
                {isUploadingVideo
                  ? `Uploading Video... ${videoUploadProgress}%`
                  : actionLoading
                    ? 'Saving...'
                    : 'Save Changes'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FeaturedAthletesScreen;